/**
 * reports-catalog.ts — Single source of truth for all 65 reports & dashboards.
 *
 * Updated: W17-ReportsCatalog-Hub
 * Do not duplicate this data elsewhere — import REPORTS_CATALOG from here.
 */

export type ReportStatus = "LIVE" | "BETA" | "COMING SOON";
export type ReportAudience = "AE" | "BDR" | "Marketing" | "Manager" | "Executive" | "Cross-role";
export type ReportCategory = string;

export interface ReportExports {
  pdf: boolean;
  xlsx: boolean;
  csv: boolean;
}

export interface ReportEntry {
  id: string;
  name: string;
  description: string;
  audience: ReportAudience;
  category: ReportCategory;
  status: ReportStatus;
  route: string | null;          // null = COMING SOON, no route yet
  exports: ReportExports;
}

const LIVE_EXPORTS: ReportExports = { pdf: true, xlsx: true, csv: true };
const BETA_EXPORTS: ReportExports = { pdf: true, xlsx: false, csv: true };
const NONE_EXPORTS: ReportExports = { pdf: false, xlsx: false, csv: false };

export const REPORTS_CATALOG: ReportEntry[] = [

  // ── AE — Pipeline & Forecast ─────────────────────────────────────────────
  {
    id: "ae-my-pipeline",
    name: "My Pipeline",
    description: "All open accounts by stage, weighted pipe, days in stage, next checkpoint needed. Sort and filter by status, value, or last touch.",
    audience: "AE",
    category: "Pipeline & Forecast",
    status: "LIVE",
    route: "/pipeline",
    exports: LIVE_EXPORTS,
  },
  {
    id: "ae-my-forecast",
    name: "My Forecast",
    description: "Commit, Best Case, Pipeline, and Closed-Won broken down by quarter and month, rolled up against quota.",
    audience: "AE",
    category: "Pipeline & Forecast",
    status: "LIVE",
    route: "/forecast",
    exports: LIVE_EXPORTS,
  },
  {
    id: "ae-forecast-accuracy",
    name: "Forecast Accuracy",
    description: "Predicted close dates vs actual, per quarter — shows forecasting discipline over time.",
    audience: "AE",
    category: "Pipeline & Forecast",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "ae-pipeline-coverage-trend",
    name: "Pipeline Coverage Trend",
    description: "Weekly coverage ratio (pipe divided by remaining quota) plotted across the quarter.",
    audience: "AE",
    category: "Pipeline & Forecast",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "ae-stage-velocity",
    name: "Stage Velocity",
    description: "Average days a deal spends in each stage in your book, vs team median.",
    audience: "AE",
    category: "Pipeline & Forecast",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── AE — Account & Opportunity ───────────────────────────────────────────
  {
    id: "ae-account-audit",
    name: "Account Audit",
    description: "Full CP1-CP10 status, answers logged, evidence count, coaching cues, and next-step recommendation for each account.",
    audience: "AE",
    category: "Account & Opportunity",
    status: "LIVE",
    route: "/account/:id",
    exports: LIVE_EXPORTS,
  },
  {
    id: "ae-accounts-in-flight",
    name: "Accounts In Flight",
    description: "Every active account with Composite score and current checkpoint coverage.",
    audience: "AE",
    category: "Account & Opportunity",
    status: "LIVE",
    route: "/reporting/accounts-in-flight",
    exports: LIVE_EXPORTS,
  },
  {
    id: "ae-decayed-accounts",
    name: "Decayed Accounts",
    description: "Accounts in your book with last touch more than 14 days ago, ranked by deal value times days decayed.",
    audience: "AE",
    category: "Account & Opportunity",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "ae-upsell-expansion-signal",
    name: "Upsell / Expansion Signal Report",
    description: "Closed-won accounts with expansion signal flagged, plus existing customers with renewal in the next 90 days.",
    audience: "AE",
    category: "Account & Opportunity",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "ae-won-lost-analysis",
    name: "Won/Lost Analysis",
    description: "Closed-won and closed-lost deals with reason codes, average deal cycle, and recurring loss patterns.",
    audience: "AE",
    category: "Account & Opportunity",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── AE — Activity ────────────────────────────────────────────────────────
  {
    id: "ae-my-activity-audit",
    name: "My Activity Audit",
    description: "Calls, emails, meetings, evidence adds, and answers logged by day across the quarter.",
    audience: "AE",
    category: "Activity",
    status: "LIVE",
    route: "/reporting/activity-audit",
    exports: LIVE_EXPORTS,
  },
  {
    id: "ae-activity-vs-outcomes",
    name: "Activity vs Outcomes",
    description: "Activity volumes correlated to stage progression and closed-won — shows which behaviors actually move deals.",
    audience: "AE",
    category: "Activity",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "ae-discovery-call-quality",
    name: "Discovery Call Quality",
    description: "Percentage of CP2 questions answered post-discovery, percentage with evidence attached, and average questions cleared per discovery call.",
    audience: "AE",
    category: "Activity",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── AE — Discipline & Coaching ───────────────────────────────────────────
  {
    id: "ae-cp-coverage-heatmap",
    name: "CP Coverage Heatmap",
    description: "Across your book, which checkpoints are cleared and which are bare — shows where qualification work is consistently weak.",
    audience: "AE",
    category: "Discipline & Coaching",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "ae-evidence-gap-report",
    name: "Evidence Gap Report",
    description: "Accounts where score is capped because of missing evidence, ranked by deal value at risk.",
    audience: "AE",
    category: "Discipline & Coaching",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "ae-coaching-history",
    name: "Coaching History",
    description: "Coaching cues triggered on your accounts, what action you took, and the outcome.",
    audience: "AE",
    category: "Discipline & Coaching",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── AE — BDR's Work in My Book ───────────────────────────────────────────
  {
    id: "ae-bdr-activity-in-my-accounts",
    name: "My BDR's Activity in My Accounts",
    description: "Every cadence step your BDR ran on your accounts: outreach attempts, dispositions, and qualified opportunities generated.",
    audience: "AE",
    category: "BDR Activity in My Book",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "ae-suspect-prospect-conversion-my-book",
    name: "Suspect to Prospect Conversion (My Book)",
    description: "Percentage of suspects your BDR worked that converted to prospects, and how long the cycle took.",
    audience: "AE",
    category: "BDR Activity in My Book",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── BDR — Cadence & Throughput ───────────────────────────────────────────
  {
    id: "bdr-my-cadence-queue",
    name: "My Cadence Queue",
    description: "Today's cadence steps, status (pending/done), and outcomes.",
    audience: "BDR",
    category: "Cadence & Throughput",
    status: "LIVE",
    route: "/daily",
    exports: LIVE_EXPORTS,
  },
  {
    id: "bdr-cadence-completion-rate",
    name: "Cadence Completion Rate",
    description: "Percentage of cadence steps completed on schedule, broken down by cadence type (call, email, LinkedIn).",
    audience: "BDR",
    category: "Cadence & Throughput",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "bdr-outbound-activity-audit",
    name: "Outbound Activity Audit",
    description: "Daily and weekly calls, emails, LinkedIn touches, and voicemails left.",
    audience: "BDR",
    category: "Cadence & Throughput",
    status: "LIVE",
    route: "/reporting/activity-audit",
    exports: LIVE_EXPORTS,
  },
  {
    id: "bdr-connect-rate",
    name: "Connect Rate",
    description: "Calls connected divided by calls dialed, by week, with industry-benchmark overlay.",
    audience: "BDR",
    category: "Cadence & Throughput",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── BDR — Qualification & Pipeline Built ─────────────────────────────────
  {
    id: "bdr-suspects-to-prospects",
    name: "Suspects to Prospects",
    description: "Your suspect-to-prospect conversion: how many worked, how many qualified, and how long the cycle took.",
    audience: "BDR",
    category: "Qualification & Pipeline Built",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "bdr-prospects-handed-off",
    name: "Prospects Handed Off",
    description: "Full list of prospects you qualified and handed to AEs, with handoff timestamp, current status, and ultimate outcome.",
    audience: "BDR",
    category: "Qualification & Pipeline Built",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "bdr-my-hunt-list",
    name: "My Hunt List",
    description: "Open-market contacts you are working, source, last activity, and next step.",
    audience: "BDR",
    category: "Qualification & Pipeline Built",
    status: "LIVE",
    route: "/dashboards/bdr",
    exports: LIVE_EXPORTS,
  },
  {
    id: "bdr-icp-match-rate",
    name: "ICP Match Rate",
    description: "Of the contacts you are working, what percentage match the ICP profile (industry, size, role). Flags off-ICP work for redirect.",
    audience: "BDR",
    category: "Qualification & Pipeline Built",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── BDR — Source Attribution ──────────────────────────────────────────────
  {
    id: "bdr-source-funnel",
    name: "Source Funnel",
    description: "Lead source to contact to suspect to prospect conversion at each stage, by source.",
    audience: "BDR",
    category: "Source Attribution",
    status: "LIVE",
    route: "/reporting/source-funnel",
    exports: LIVE_EXPORTS,
  },
  {
    id: "bdr-source-attribution-multi-touch",
    name: "Source Attribution (multi-touch)",
    description: "Credit assigned across multiple touchpoints when a deal closes.",
    audience: "BDR",
    category: "Source Attribution",
    status: "LIVE",
    route: "/reports/source-attribution",
    exports: LIVE_EXPORTS,
  },

  // ── BDR — Discipline ──────────────────────────────────────────────────────
  {
    id: "bdr-cadence-adherence-by-account",
    name: "Cadence Adherence by Account",
    description: "Per-account view of where you are on cadence and where you have slipped.",
    audience: "BDR",
    category: "Discipline",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "bdr-dial-time-audit",
    name: "Dial Time Audit",
    description: "Total time on phone per day, distribution across hours, and peak-effectiveness windows.",
    audience: "BDR",
    category: "Discipline",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── Marketing — Funnel ────────────────────────────────────────────────────
  {
    id: "mkt-marketing-funnel",
    name: "Marketing Funnel",
    description: "Open-market contacts to leads to suspects to prospects, with filters and monthly trend.",
    audience: "Marketing",
    category: "Funnel",
    status: "LIVE",
    route: "/dashboards/marketing",
    exports: LIVE_EXPORTS,
  },
  {
    id: "mkt-lead-source-performance",
    name: "Lead Source Performance",
    description: "Which channels (paid, organic, partner, events, content, syndication) produce the highest-converting leads.",
    audience: "Marketing",
    category: "Funnel",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "mkt-conversion-rate-by-stage",
    name: "Conversion Rate by Stage",
    description: "Monthly conversion rate at each funnel stage, vs prior month.",
    audience: "Marketing",
    category: "Funnel",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── Marketing — Campaigns ─────────────────────────────────────────────────
  {
    id: "mkt-campaign-performance",
    name: "Campaign Performance",
    description: "Per-campaign attribution: leads generated, suspects produced, prospects, closed-won attributed, and ROI.",
    audience: "Marketing",
    category: "Campaigns",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "mkt-content-engagement",
    name: "Content Engagement",
    description: "Content drops (LinkedIn drip, blog, email) with open, click, and reply rates.",
    audience: "Marketing",
    category: "Campaigns",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "mkt-event-pipeline-influence",
    name: "Event Pipeline Influence",
    description: "Events run with attendees-to-pipeline conversion and deal value influenced.",
    audience: "Marketing",
    category: "Campaigns",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── Marketing — Cadence & Activation ─────────────────────────────────────
  {
    id: "mkt-sourced-cadence-performance",
    name: "Marketing-Sourced Cadence Performance",
    description: "For leads marketing handed to BDR, how many entered cadence and how many converted.",
    audience: "Marketing",
    category: "Cadence & Activation",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "mkt-hooks-library-performance",
    name: "Marketing Hooks Library Performance",
    description: "Outreach hooks usage and reply rate per hook.",
    audience: "Marketing",
    category: "Cadence & Activation",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── Manager — Team Performance ────────────────────────────────────────────
  {
    id: "mgr-team-scoreboard",
    name: "Team Scoreboard",
    description: "Every rep ranked by quota percentage, weighted pipe, activity volume, and accounts in flight.",
    audience: "Manager",
    category: "Team Performance",
    status: "LIVE",
    route: "/reporting/team-scoreboard",
    exports: LIVE_EXPORTS,
  },
  {
    id: "mgr-rep-detail-drilldown",
    name: "Rep Detail Drill-down",
    description: "Full activity and pipeline view per rep.",
    audience: "Manager",
    category: "Team Performance",
    status: "LIVE",
    route: "/reporting/rep",
    exports: LIVE_EXPORTS,
  },
  {
    id: "mgr-forecast-rollup",
    name: "Forecast Roll-up",
    description: "Team forecast vs quota with rep-by-rep breakdown.",
    audience: "Manager",
    category: "Team Performance",
    status: "LIVE",
    route: "/reports/forecast-rollup",
    exports: LIVE_EXPORTS,
  },
  {
    id: "mgr-composite-scoring",
    name: "Composite Scoring Report",
    description: "Composite scores across the team's book with distribution.",
    audience: "Manager",
    category: "Team Performance",
    status: "LIVE",
    route: "/reports/composite-scoring",
    exports: LIVE_EXPORTS,
  },

  // ── Manager — Discipline & Quality ───────────────────────────────────────
  {
    id: "mgr-data-hygiene",
    name: "Data Hygiene",
    description: "Missing fields, decayed records, and evidence gaps across the team.",
    audience: "Manager",
    category: "Discipline & Quality",
    status: "LIVE",
    route: "/reporting/data-hygiene",
    exports: LIVE_EXPORTS,
  },
  {
    id: "mgr-push-adequacy",
    name: "Push Adequacy",
    description: "Whether checkpoint push decisions were backed by enough evidence.",
    audience: "Manager",
    category: "Discipline & Quality",
    status: "LIVE",
    route: "/reports/push-adequacy",
    exports: LIVE_EXPORTS,
  },
  {
    id: "mgr-push-reentry",
    name: "Push Re-entry",
    description: "Accounts that were pushed and re-entered, with reason.",
    audience: "Manager",
    category: "Discipline & Quality",
    status: "LIVE",
    route: "/reports/push-reentry",
    exports: LIVE_EXPORTS,
  },
  {
    id: "mgr-soft-stop-audit",
    name: "Soft Stop Audit",
    description: "Soft-stopped deals with full audit trail.",
    audience: "Manager",
    category: "Discipline & Quality",
    status: "LIVE",
    route: "/reports/soft-stop-audit",
    exports: LIVE_EXPORTS,
  },
  {
    id: "mgr-escalation-queue",
    name: "Escalation Queue",
    description: "Accounts flagged for manager review.",
    audience: "Manager",
    category: "Discipline & Quality",
    status: "LIVE",
    route: "/reports/escalation-queue",
    exports: LIVE_EXPORTS,
  },
  {
    id: "mgr-external-consultants",
    name: "External Consultants",
    description: "Third-party involvement on deals.",
    audience: "Manager",
    category: "Discipline & Quality",
    status: "LIVE",
    route: "/reports/external-consultants",
    exports: LIVE_EXPORTS,
  },

  // ── Manager — Coaching ────────────────────────────────────────────────────
  {
    id: "mgr-coaching-triggers-by-rep",
    name: "Coaching Triggers by Rep",
    description: "Which coaching cues fired on each rep this week and what intervention was logged.",
    audience: "Manager",
    category: "Coaching",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "mgr-discipline-defense-log",
    name: "Discipline Defense Log",
    description: "When reps overrode system warnings, what they did, and what happened.",
    audience: "Manager",
    category: "Coaching",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "mgr-1on1-prep-sheet",
    name: "1:1 Prep Sheet (per rep)",
    description: "Auto-generated brief for Monday 1:1s: rep's week, biggest wins, biggest slips, and coaching priorities.",
    audience: "Manager",
    category: "Coaching",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── Manager — Team Trends ─────────────────────────────────────────────────
  {
    id: "mgr-weekly-movement-report",
    name: "Weekly Movement Report",
    description: "Accounts forward, backward, qualified, and lost per rep — the weekly summary for team review.",
    audience: "Manager",
    category: "Team Trends",
    status: "LIVE",
    route: "/reporting/team-scoreboard",
    exports: LIVE_EXPORTS,
  },
  {
    id: "mgr-stage-conversion-by-team",
    name: "Stage Conversion by Team",
    description: "Team-level conversion rates at each checkpoint — identifies where the team is structurally weak.",
    audience: "Manager",
    category: "Team Trends",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── Executive — Top-of-House ──────────────────────────────────────────────
  {
    id: "exec-executive-overview",
    name: "Executive Overview Dashboard",
    description: "Quota, weighted pipe, forecast, AE scoreboard, and coverage — the single board-ready view.",
    audience: "Executive",
    category: "Top-of-House",
    status: "LIVE",
    route: "/dashboards/executive",
    exports: LIVE_EXPORTS,
  },
  {
    id: "exec-quarter-forecast-confidence",
    name: "Quarter Forecast Confidence",
    description: "Confidence interval on quarterly close with sensitivity analysis on the top five deals.",
    audience: "Executive",
    category: "Top-of-House",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "exec-pipeline-trend-52-week",
    name: "Pipeline Trend (52-week)",
    description: "Weekly pipeline value over rolling year with seasonal markers.",
    audience: "Executive",
    category: "Top-of-House",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── Executive — Strategic ─────────────────────────────────────────────────
  {
    id: "exec-industry-mix",
    name: "Industry Mix",
    description: "Pipeline and closed-won by industry vertical.",
    audience: "Executive",
    category: "Strategic",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "exec-deal-size-distribution",
    name: "Deal Size Distribution",
    description: "Deals by size band with win rates per band.",
    audience: "Executive",
    category: "Strategic",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "exec-sales-cycle-length",
    name: "Sales Cycle Length",
    description: "Average days from suspect to closed-won, with distribution and outliers.",
    audience: "Executive",
    category: "Strategic",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "exec-customer-lifetime-value",
    name: "Customer Lifetime Value",
    description: "Closed-won accounts tracked across expansion and renewal.",
    audience: "Executive",
    category: "Strategic",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── Executive — Risk ──────────────────────────────────────────────────────
  {
    id: "exec-concentration-risk",
    name: "Concentration Risk",
    description: "Percentage of pipe in top-five accounts, top-three industries, and single customer concentration.",
    audience: "Executive",
    category: "Risk",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "exec-at-risk-forecast",
    name: "At-Risk Forecast",
    description: "Commit deals with red checkpoints, missed sign-off dates, and decayed touchpoints.",
    audience: "Executive",
    category: "Risk",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },

  // ── Cross-role / System-wide ──────────────────────────────────────────────
  {
    id: "cross-rep-quota-report",
    name: "Rep Quota Report",
    description: "Quota set vs actual per rep.",
    audience: "Cross-role",
    category: "System-wide",
    status: "LIVE",
    route: "/reports/rep-quota",
    exports: LIVE_EXPORTS,
  },
  {
    id: "cross-question-bank-coverage",
    name: "Question Bank Coverage",
    description: "Percentage of checkpoint questions answered across the org, by checkpoint.",
    audience: "Cross-role",
    category: "System-wide",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
  {
    id: "cross-methodology-adherence-score",
    name: "Methodology Adherence Score",
    description: "Per-rep score on how closely they follow the M-PACT framework.",
    audience: "Cross-role",
    category: "System-wide",
    status: "COMING SOON",
    route: null,
    exports: NONE_EXPORTS,
  },
];

// ── Derived helpers ──────────────────────────────────────────────────────────

export const AUDIENCE_ORDER: ReportAudience[] = [
  "AE",
  "BDR",
  "Marketing",
  "Manager",
  "Executive",
  "Cross-role",
];

export function getCatalogByAudience(): Record<ReportAudience, ReportEntry[]> {
  const result = {} as Record<ReportAudience, ReportEntry[]>;
  for (const aud of AUDIENCE_ORDER) {
    result[aud] = REPORTS_CATALOG.filter((r) => r.audience === aud);
  }
  return result;
}

export function getCatalogStats() {
  const total = REPORTS_CATALOG.length;
  const live = REPORTS_CATALOG.filter((r) => r.status === "LIVE").length;
  const beta = REPORTS_CATALOG.filter((r) => r.status === "BETA").length;
  const comingSoon = REPORTS_CATALOG.filter((r) => r.status === "COMING SOON").length;
  return { total, live, beta, comingSoon };
}
